import type { HubConnection } from '@microsoft/signalr';
import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';

import { modeHeaderKey } from 'features/multi-mode/constants';
import type { Mode } from 'features/multi-mode/enums';
import type { ClientNotificationEvent } from 'features/rns/interfaces';
import { RnsEventBus } from 'features/rns/services';
import { generateSignalRReconnectDelays } from 'features/rns/helpers';

interface RnsConnectionParams {
  host: string;
  token: string;
  mode: Mode;
}

interface ClientNotificationParams extends ClientNotificationEvent {
  receiverId: string;
}

export class RnsConnectionService {
  private static instance: RnsConnectionService | undefined;
  private readonly connection!: HubConnection;
  /**
   * SignalR fires `onclose` for an application-requested stop and for a dropped
   * transport alike. Consumers have to react very differently to the two, so the
   * service records which one is happening.
   */
  private isShuttingDown = false;

  private constructor({ host, mode, token }: RnsConnectionParams) {
    this.connection = new HubConnectionBuilder()
      .withUrl(`${host}/?${modeHeaderKey}=${mode}`, {
        transport: HttpTransportType.WebSockets,
        headers: {
          Authorization: token,
          [modeHeaderKey]: mode,
        },
      })
      .withAutomaticReconnect(generateSignalRReconnectDelays())
      .build();

    this.connection.onclose((error?: Error) => {
      const state = this.connection.state;
      const connectionId = this.connection.connectionId;
      const baseUrl = this.connection.baseUrl;
      const isIntentional = this.isShuttingDown;

      if (error) {
        console.error('RNS connection closed with error:', {
          error: error.message,
          connectionId,
          state,
          baseUrl,
          isIntentional,
        });
      } else {
        console.info('RNS connection closed normally', {
          connectionId,
          state,
          baseUrl,
          isIntentional,
        });
      }

      RnsEventBus.$close.next({
        error,
        connectionId,
        state,
        baseUrl,
        isIntentional,
      });
    });

    this.connection.onreconnecting((error) => {
      const state = this.connection.state;
      const connectionId = this.connection.connectionId;

      console.warn('SignalR connection reconnecting:', {
        error: error?.message,
        connectionId,
        state,
      });

      RnsEventBus.$reconnecting.next({
        error: error || new Error('Reconnecting without error details'),
        connectionId,
        state,
      });
    });

    this.connection.onreconnected((connectionId) => {
      const state = this.connection.state;
      const previousConnectionId = this.connection.connectionId;

      console.info('SignalR connection reestablished:', {
        newConnectionId: connectionId,
        oldConnectionId: previousConnectionId,
        state,
      });

      RnsEventBus.$reconnected.next({
        connectionId,
        previousConnectionId,
        state,
      });
    });
  }

  public static getInstance(
    params?: RnsConnectionParams
  ): RnsConnectionService | null {
    if (RnsConnectionService.instance) {
      return RnsConnectionService.instance;
    }

    if (!RnsConnectionService.instance && params) {
      RnsConnectionService.instance = new RnsConnectionService(params);
      return RnsConnectionService.instance;
    }

    console.warn('There is no RNS instance and parameters were not provided');
    return null;
  }

  public getConnection(): HubConnection {
    return this.connection;
  }

  public async start() {
    // A start that loses the race with cleanup rejects, and the caller retries.
    // Retrying a discarded service would connect only to hang up again, so the
    // attempt stops here instead.
    if (this.isShuttingDown) {
      console.info('Skipping an RNS start for a connection being torn down', {
        connectionId: this.connection.connectionId,
      });
      return;
    }

    if (this.connection.state === HubConnectionState.Disconnected) {
      console.info('Starting SignalR connection...', {
        connectionId: this.connection.connectionId,
        state: this.connection.state,
        baseUrl: this.connection.baseUrl,
      });

      try {
        await this.connection.start();
        console.info('SignalR connection started successfully', {
          connectionId: this.connection.connectionId,
        });

        // Cleanup can land while this start is still in flight. Without this
        // check the connection would join the group and announce itself after
        // the service was already discarded, leaving a live connection that
        // nothing owns and nothing can stop.
        if (this.isShuttingDown) {
          console.info(
            'Discarding an RNS connection that started after cleanup',
            {
              connectionId: this.connection.connectionId,
            }
          );
          await this.connection.stop();
          return;
        }

        await this.connection.send('GroupJoin', 'AgentStations');
        console.info('Joined AgentStations group');

        RnsEventBus.$connected.next({
          connection: this.connection,
          connectionId: this.connection.connectionId,
          state: this.connection.state,
          baseUrl: this.connection.baseUrl,
        });
      } catch (error) {
        console.error('Failed to start SignalR connection:', {
          error,
          connectionState: this.connection.state,
        });
        throw error;
      }
    }
  }

  public async sendClientNotification({
    eventName,
    receiverId,
    data,
  }: ClientNotificationParams) {
    await this.connection.send(
      'NotifyClient',
      receiverId,
      eventName,
      JSON.stringify(data ?? {})
    );
  }

  public static async cleanup(): Promise<void> {
    const instance = RnsConnectionService.instance;

    if (!instance) {
      return;
    }

    instance.isShuttingDown = true;
    // Clearing the slot up front keeps a later caller from handing out an
    // instance that is already shutting down.
    RnsConnectionService.instance = undefined;

    if (instance.connection.state !== HubConnectionState.Disconnected) {
      await instance.connection.stop();
    }

    console.info('RNS connection cleanup');
  }
}
